// src/components/ActiveSearchFilterChips/ActiveSearchFilterChips.jsx

'use client';

import { useSearchParams, usePathname, useRouter } from 'next/navigation';
import { Chip, Box } from '@mui/material';
import { useTransition } from 'react';
import styles from '@/app/[lang]/shop/shop-category.module.scss';


const filterKeys = [
  'size', 'mainColors', 'styles', 'prices', 'manufacturing',
  'weavedMaterials', 'origins', 'productID', 'delivery',
  'usage', 'pileThickness'
];

const chipStyles = {
  margin: '2px',
  height: 'auto',
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    display: 'block',
    lineHeight: '1.2em',
    padding: '8px 12px',
  },
};

export default function ActiveSearchFilterChips({ lang, labels }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();

  // Calculate active filters
  const activeFilters = [];
  for (const key of filterKeys) {
    const value = searchParams.get(key);
    if (value) {
      activeFilters.push({ key, value });
    }
  }

  // Handler for deleting a filter
  const handleDelete = (key) => {
    startTransition(() => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete(key);
      const newQuery = params.toString();
      router.push(`${pathname}${newQuery ? `?${newQuery}` : ''}`);
    });
  };

  // Handler for clearing all filters
  const handleClearAll = () => {
    startTransition(() => {
      const params = new URLSearchParams(searchParams.toString());
      filterKeys.forEach((key) => params.delete(key));
      const newQuery = params.toString();
      router.push(`${pathname}${newQuery ? `?${newQuery}` : ''}`);
    });
  };

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <Box 
      sx={{ padding: 2, opacity: isPending ? 0.6 : 1 }} 
      className={styles.chipsContainer}
    >
      <h2 style={{ fontSize: '1em' }}>{labels.activeFilters}</h2>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {activeFilters.map(({ key, value }) => (
          <Chip
            key={key}
            label={`${labels[key]}: ${value}`}
            onDelete={() => handleDelete(key)}
            sx={chipStyles}
            disabled={isPending}
          />
        ))}
        {activeFilters.length > 2 && (
          <Chip
            label={labels.clearFilters}
            onClick={handleClearAll}
            sx={{
              ...chipStyles,
              backgroundColor: 'rgba(224,224,224,.63)',
              fontWeight: 'bold',
            }}
            disabled={isPending}
          />
        )}
      </Box>
    </Box>
  );
}
